import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import 'iconify-icon';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import { resolveState } from '../data/brazil-state-centroids';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Role badge config — colors per spec
const ROLE_STYLES = {
  Franqueado: 'bg-accent-orange text-white',
  Trainer: 'bg-[#EAEAEA] text-black border border-[#EAEAEA]',
  Comercial: 'bg-[#555555] text-white',
};

const FUNCTION_FILTERS = [
  { key: 'all', label: 'Todos' },
  { key: 'Franqueado', label: 'Franqueados' },
  { key: 'Trainer', label: 'Trainers' },
  { key: 'Comercial', label: 'Comercial' },
];

/**
 * LeadershipCard removed by user request — directors now appear inline
 * in the regular grid (sorted first) with only their corporate_role
 * surfaced above the role badges.
 */

const PersonCard = ({ m, isAdmin }) => {
  const stateCode = m.state_code || m.state;
  const igHandle = m.instagram ? m.instagram.replace('@', '') : null;
  return (
    <article
      data-testid={`team-card-${m.name.replace(/\s+/g, '-').toLowerCase()}`}
      className="relative bg-[#0F0F0F] border border-[#333333] rounded-2xl p-6 text-center hover:border-accent-orange transition-colors"
    >
      {isAdmin && (
        <Link
          to={`/admin/franchises?edit=${m.franchise_id}`}
          data-testid={`team-card-edit-${m.franchise_id}-${m.name.replace(/\s+/g, '-').toLowerCase()}`}
          title={m.photo ? 'Editar foto deste Eneagramado' : 'Adicionar foto deste Eneagramado'}
          className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 px-2.5 py-1 bg-accent-orange/95 hover:bg-accent-orange text-white rounded-full text-[10px] font-bold uppercase tracking-wider shadow-lg transition-transform hover:scale-105"
        >
          <iconify-icon
            icon={m.photo ? 'solar:pen-2-linear' : 'solar:camera-add-linear'}
            className="text-sm"
          ></iconify-icon>
          {m.photo ? 'Editar' : 'Foto'}
        </Link>
      )}

      {m.photo ? (
        <img
          src={m.photo}
          alt={m.name}
          className="w-24 h-24 rounded-full object-cover mx-auto mb-4 ring-2 ring-[#333333]"
        />
      ) : (
        <div className="w-24 h-24 rounded-full bg-[#1F1F1F] border border-[#333333] flex items-center justify-center mx-auto mb-4 text-accent-orange">
          <iconify-icon icon="solar:user-circle-linear" className="text-5xl"></iconify-icon>
        </div>
      )}

      <h3 className="font-bold text-lg text-[#EAEAEA] mb-2">{m.name}</h3>

      {m.corporate_role && (
        <p
          className="text-[10px] font-mono uppercase tracking-widest text-accent-orange mb-2"
          data-testid="team-card-corporate-role"
        >
          {m.corporate_role}
        </p>
      )}

      {/* Role badges (multi) */}
      <div className="flex flex-wrap justify-center gap-1.5 mb-3">
        {(m.roles || []).map((r) => (
          <span
            key={`${m.name}-${r}`}
            data-testid={`team-badge-${r.toLowerCase()}`}
            className={`px-2.5 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider ${ROLE_STYLES[r] || 'bg-neutral-700 text-white'}`}
          >
            {r}
          </span>
        ))}
      </div>

      <Link
        to={`/franquia/${m.franchise_id}`}
        className="block text-xs text-neutral-500 hover:text-accent-orange mb-1 line-clamp-1 transition-colors"
      >
        {m.franchise_name}
      </Link>

      <p className="text-[11px] text-neutral-600 mb-4">
        {m.city}
        {stateCode && ` · ${stateCode}`}
      </p>

      {igHandle && (
        <a
          href={`https://instagram.com/${igHandle}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-accent-orange transition-colors"
        >
          <iconify-icon icon="mdi:instagram"></iconify-icon>
          {m.instagram}
        </a>
      )}
    </article>
  );
};

/**
 * /equipe — Aggregates all team[] members across franchises. Each member
 * carries `roles[]` (Franqueado | Trainer | Comercial), `city`, `state`
 * (UF) and `instagram`. Filters: function + state.
 */
const TeamPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [franchises, setFranchises] = useState([]);
  const [leadership, setLeadership] = useState([]);
  const [selectedFunction, setSelectedFunction] = useState('all');
  const [selectedState, setSelectedState] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [fr, ld] = await Promise.all([
          axios.get(`${API_URL}/public/franchises`),
          axios.get(`${API_URL}/public/leadership`).catch(() => ({ data: [] })),
        ]);
        setFranchises(Array.isArray(fr.data) ? fr.data : []);
        setLeadership(Array.isArray(ld.data) ? ld.data : []);
      } catch (e) {
        console.warn('Failed to load equipe data', e?.message);
      } finally {
        setLoading(false);
      }
    })();
    window.scrollTo(0, 0);
  }, []);

  // Flatten team[] from every franchise. Prefer member.state (UF) over
  // franchise.state because a single franchise can host members from
  // multiple cities/UFs. Then merge corporate leadership (CEO, directors)
  // by name — same person can be both Diretora AND Trainer.
  const people = useMemo(() => {
    const byName = new Map();

    franchises.forEach((f) => {
      (f.team || []).forEach((t) => {
        if (!t || !t.name) return;
        const memberStateMeta = resolveState(t.state || f.state);
        byName.set(t.name, {
          name: t.name,
          roles: Array.isArray(t.roles) && t.roles.length
            ? t.roles
            : (t.role ? [t.role] : ['Trainer']),
          photo: t.photo || null,
          instagram: t.instagram || null,
          city: t.city || f.city,
          state: t.state || f.state,
          state_code: memberStateMeta?.code || null,
          franchise_id: f.franchise_id,
          franchise_name: f.name,
          corporate_role: t.corporate_role || null,
        });
      });
    });

    // Merge leadership (separate collection). If the person already exists
    // from franchise.team[], just enrich with corporate_role; otherwise add.
    leadership.forEach((l) => {
      const existing = byName.get(l.name);
      if (existing) {
        existing.corporate_role = existing.corporate_role || l.corporate_role;
        if (!existing.photo && l.photo) existing.photo = l.photo;
        if (!existing.instagram && l.instagram) existing.instagram = l.instagram;
      } else {
        byName.set(l.name, {
          name: l.name,
          roles: (l.roles || []).filter((r) => r !== 'Diretoria'),
          photo: l.photo || null,
          instagram: l.instagram || null,
          city: l.city || null,
          state: l.state || null,
          state_code: l.state ? resolveState(l.state)?.code : null,
          franchise_id: l.franchise_id || null,
          franchise_name: null,
          corporate_role: l.corporate_role,
        });
      }
    });

    return Array.from(byName.values());
  }, [franchises, leadership]);

  // States that have at least one person (drives the state filter bar)
  const availableStates = useMemo(() => {
    const codes = new Set();
    people.forEach((p) => {
      if (p.state_code) codes.add(p.state_code);
    });
    // Display in user-requested order, but only those present
    const desired = ['RS', 'SC', 'PR', 'SP', 'MG', 'GO', 'DF'];
    const ordered = desired.filter((c) => codes.has(c));
    // Append any extras that appear in data but aren't in the desired list
    Array.from(codes).forEach((c) => {
      if (!desired.includes(c)) ordered.push(c);
    });
    return ordered;
  }, [people]);

  const filtered = useMemo(() => {
    return people.filter((p) => {
      if (selectedFunction !== 'all' && !p.roles.includes(selectedFunction)) {
        return false;
      }
      if (selectedState !== 'all' && p.state_code !== selectedState) {
        return false;
      }
      return true;
    });
  }, [people, selectedFunction, selectedState]);

  // Count by role for label tabs
  const counts = useMemo(() => {
    const c = { all: people.length, Franqueado: 0, Trainer: 0, Comercial: 0 };
    people.forEach((p) => p.roles.forEach((r) => {
      if (r in c) c[r] += 1;
    }));
    return c;
  }, [people]);

  // Sort filtered list: corporate leaders first (CEO/Diretora at top),
  // then Franqueados, then Trainers, then Comercial; alpha within each.
  const sortedFiltered = useMemo(() => {
    const weight = (p) => {
      if (p.corporate_role) return -1;
      if (p.roles.includes('Franqueado')) return 0;
      if (p.roles.includes('Trainer')) return 1;
      return 2;
    };
    return [...filtered].sort((a, b) => {
      const wa = weight(a);
      const wb = weight(b);
      if (wa !== wb) return wa - wb;
      return a.name.localeCompare(b.name, 'pt-BR');
    });
  }, [filtered]);

  const pillBase =
    'px-5 py-2.5 rounded-full text-xs font-mono uppercase tracking-widest transition-colors whitespace-nowrap';

  return (
    <>
      <SEO
        title="Equipe IE — Franqueados, Trainers e Comercial"
        description="Conheça os profissionais ativos do Instituto Eneagrama: franqueados, trainers certificados e equipe comercial em todas as franquias do Brasil."
        path="/equipe"
      />
      <Header />
      <main className="min-h-screen">
        {/* HERO */}
        <section
          className="relative z-10 pt-32 pb-12 border-b border-neutral-900"
          style={{ background: 'rgba(10, 10, 10, 0.78)' }}
        >
          <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 text-center">
            <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent-orange mb-4 block">
              A equipe do Instituto Eneagrama
            </span>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-[#EAEAEA] mb-4">
              Nossa equipe
            </h1>
            <p className="text-lg text-neutral-400 max-w-3xl mx-auto">
              {counts.all} profissionais ativos em {availableStates.length} estados — franqueados,
              trainers certificados e equipe comercial conduzindo os programas IE no Brasil.
            </p>
          </div>
        </section>

        {/* FILTERS */}
        <section
          className="relative z-10 py-6 border-b border-neutral-900"
          style={{ background: 'rgba(17, 17, 17, 0.85)' }}
        >
          <div className="max-w-6xl mx-auto px-6 md:px-12 space-y-4">
            {/* Function filter */}
            <div
              className="flex flex-wrap justify-center gap-2"
              data-testid="team-function-filters"
            >
              {FUNCTION_FILTERS.map((f) => {
                const active = selectedFunction === f.key;
                const n = counts[f.key] ?? 0;
                return (
                  <button
                    key={f.key}
                    onClick={() => setSelectedFunction(f.key)}
                    data-testid={`team-fn-filter-${f.key.toLowerCase()}`}
                    className={`${pillBase} ${
                      active
                        ? 'bg-accent-orange text-white'
                        : 'bg-[#1A1A1A] border border-[#333333] text-neutral-400 hover:border-accent-orange hover:text-accent-orange'
                    }`}
                  >
                    {f.label} <span className="opacity-70 ml-1">({n})</span>
                  </button>
                );
              })}
            </div>

            {/* State filter */}
            <div
              className="flex flex-wrap justify-center gap-2"
              data-testid="team-state-filters"
            >
              <button
                onClick={() => setSelectedState('all')}
                data-testid="team-state-filter-all"
                className={`${pillBase} ${
                  selectedState === 'all'
                    ? 'bg-[#EAEAEA] text-black'
                    : 'bg-[#1A1A1A] border border-[#333333] text-neutral-400 hover:border-[#EAEAEA] hover:text-[#EAEAEA]'
                }`}
              >
                Todos os estados
              </button>
              {availableStates.map((code) => (
                <button
                  key={code}
                  onClick={() => setSelectedState(code)}
                  data-testid={`team-state-filter-${code}`}
                  className={`${pillBase} ${
                    selectedState === code
                      ? 'bg-[#EAEAEA] text-black'
                      : 'bg-[#1A1A1A] border border-[#333333] text-neutral-400 hover:border-[#EAEAEA] hover:text-[#EAEAEA]'
                  }`}
                >
                  {code}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* RESULTS */}
        <section
          className="relative z-10 py-20"
          style={{ background: 'rgba(10, 10, 10, 0.78)' }}
        >
          <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12">
            {loading ? (
              <div className="text-center py-12 text-neutral-500">Carregando equipe…</div>
            ) : sortedFiltered.length === 0 ? (
              <div className="text-center py-12 text-neutral-500" data-testid="team-empty-state">
                <iconify-icon
                  icon="solar:users-group-rounded-linear"
                  className="text-6xl mb-4"
                ></iconify-icon>
                <p className="mb-2">
                  Nenhum profissional encontrado com este filtro.
                </p>
                <button
                  onClick={() => {
                    setSelectedFunction('all');
                    setSelectedState('all');
                  }}
                  className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-accent-orange hover:text-[#E55D00]"
                >
                  Limpar filtros →
                </button>
              </div>
            ) : (
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                data-testid="team-grid"
              >
                {sortedFiltered.map((m) => (
                  <PersonCard
                    key={`${m.franchise_id}-${m.name}`}
                    m={m}
                    isAdmin={isAdmin}
                  />
                ))}
              </div>
            )}

            {/* Total count */}
            {!loading && sortedFiltered.length > 0 && (
              <div
                className="mt-12 text-center text-xs font-mono uppercase tracking-widest text-neutral-500"
                data-testid="team-count"
              >
                {sortedFiltered.length} profissiona{sortedFiltered.length > 1 ? 'is' : 'l'}
                {selectedFunction !== 'all' && ` · ${FUNCTION_FILTERS.find((x) => x.key === selectedFunction)?.label}`}
                {selectedState !== 'all' && ` · ${selectedState}`}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default TeamPage;
